import { APP_ROUTES } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import type { PatientScreenerAnswers, UserRole } from "@src/context/ApplicationContext";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useIdleFormFlush } from "@src/hooks/useIdleFormFlush";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { buildOutboundPayload, CANVAS_OUTBOUND_EVENT_NAME } from "@src/services/canvas/outbound";
import type { ScreenerAnswer } from "@src/services/screener-answers.service";
import { postScreenerAnswers } from "@src/services/screener-answers.service";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import { resolveScreenerQuestions } from "@src/utils/page-labels-resolver";
import { canRoleBypassGuardrails } from "@src/utils/role-guardrails";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { Text } from "@uhg-abyss/web/ui/Text";
import { Fragment, type ReactNode, type SyntheticEvent, useCallback, useState } from "react";
import { FormFooter } from "./FormFooter";
import {
  NAVIGATOR_SCREENER_QUESTIONS,
  PATIENT_SCREENER_QUESTIONS,
} from "./patient-screener-questions";

export type { PatientScreenerAnswers };

// Maps question id → PatientScreenerAnswers key (single source of truth)
const QUESTION_KEY_MAP: Record<number, keyof PatientScreenerAnswers> = {
  1: "hospitalized",
  2: "eatingDisorder",
  3: "seriousMentalIllness",
  4: "longActingInjectable",
  5: "opioidTreatment",
  6: "controlledSubstances",
};

// Internal state indexed by question id
type AnswerState = Record<number, boolean | undefined>;
type ErrorState = Record<number, string>;

const initialAnswers = (): AnswerState => {
  return Object.fromEntries(
    PATIENT_SCREENER_QUESTIONS.map(({ id }) => {
      return [id, undefined];
    }),
  );
};

// Maps local AnswerState (keyed by question id) → PatientScreenerAnswers (keyed by field name)
const mapAnswersToPatientScreenerAnswers = (state: AnswerState): PatientScreenerAnswers => {
  return Object.fromEntries(
    Object.entries(QUESTION_KEY_MAP).map(([id, key]) => {
      return [key, state[Number(id)]];
    }),
  ) as PatientScreenerAnswers;
};

interface ToggleInputProps {
  id: number;
  label: ReactNode;
  value: boolean | undefined;
  onChange: (id: number, value: boolean) => void;
}

const QuestionBlock = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  padding: "8px 0",
  borderBottom: "1px solid $app.color.border.subtle",
  "&:last-child": {
    borderBottom: "none",
  },
});

const ButtonGroup = styled("fieldset", {
  display: "flex",
  gap: "8px",
  alignItems: "center",
  border: "none",
  margin: 0,
  padding: 0,
});

const VisuallyHiddenLegend = styled("legend", {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
});

const TOGGLE_OPTIONS = [
  { label: "Yes", boolValue: true },
  { label: "No", boolValue: false },
] as const;

const ToggleInput = ({ id, label, value, onChange }: ToggleInputProps) => (
  <ButtonGroup data-testid={`test-id-patient-screener-q${id}-button-group`}>
    <VisuallyHiddenLegend id={`obc-question-${id}`}>{label}</VisuallyHiddenLegend>
    {TOGGLE_OPTIONS.map(({ label, boolValue }) => {
      const slug = label.toLowerCase();
      const isActive = value === boolValue;
      return (
        <Button
          key={slug}
          data-testid={`test-id-patient-screener-q${id}-${slug}-button`}
          id={`obc-q${id}-${slug}`}
          data-id={`obc-q${id}-${slug}`}
          data-obc-label={`q${id}-${slug}`}
          aria-pressed={isActive}
          onClick={() => onChange(id, boolValue)}
          type="button"
          size="sm"
          variant={isActive ? "filled" : "outline"}
        >
          {label}
        </Button>
      );
    })}
  </ButtonGroup>
);

const ControlColumn = styled("div", {
  paddingTop: "$core.spacing.300",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  minWidth: "120px",
});

const QuestionContent = styled("div", {
  paddingTop: "$core.spacing.300",
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

const ErrorRow = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "4px",
});

const DisclaimerRow = styled("div", {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  marginTop: "8px",
  width: "100%",
});

const RequiredMarker = styled("span", {
  color: "$app.color.text.status.error",
  marginLeft: "4px",
});

type PatientScreenerFormProps = {};

export const PatientScreenerForm = (_props: PatientScreenerFormProps) => {
  const { data, setData } = useApplicationContext();
  const { isInSalesforce, publishToSalesforce } = useCanvasContext();
  const { FEATURE_LABEL } = useFeatureFlags();
  const isFeatureLabelEnabled = Boolean(FEATURE_LABEL);
  const userRole = data?.userRole;
  const shouldBypassBlockingGuards = canRoleBypassGuardrails(userRole);
  let age: number | undefined;
  if (data?.ageRange) {
    age = isUnder18AgeRange(data.ageRange) ? 0 : 18;
  }
  const questions = resolveScreenerQuestions(
    PATIENT_SCREENER_QUESTIONS,
    NAVIGATOR_SCREENER_QUESTIONS,
    userRole,
    {
      enabled: isFeatureLabelEnabled,
      age,
    },
  );
  const navigate = useNavigateWithUTM();
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const [answers, setAnswers] = useState<AnswerState>(() => {
    const saved = data?.patientScreenerAnswers;
    if (!saved) return initialAnswers();
    return Object.fromEntries(
      Object.entries(QUESTION_KEY_MAP).map(([id, key]) => {
        return [Number(id), (saved as any)[key]];
      }),
    ) as AnswerState;
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const { runExclusive, isPending, isUiBlocked } = useExclusiveAction("patient-screener:next");

  // ─── Draft-save on "I'm here" ─────────────────────────────────
  // Screener answers are already persisted to context on each change via
  // handleChange, but this ensures any final partial state is captured.
  const saveToContext = useCallback(() => {
    setData("patientScreenerAnswers", mapAnswersToPatientScreenerAnswers(answers));
  }, [answers, setData]);

  useIdleFormFlush(saveToContext);

  const handleChange = (id: number, value: boolean) => {
    setAnswers((prev) => {
      const next = { ...prev, [id]: value };
      // Persist to context so answers survive navigation
      setData("patientScreenerAnswers", mapAnswersToPatientScreenerAnswers(next));
      return next;
    });
    if (errors[id]) {
      setErrors((prev) => {
        return { ...prev, [id]: "" };
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: ErrorState = {};
    const requiredMsg = "Please select an answer";
    questions.forEach(({ id }) => {
      if (answers[id] === undefined) {
        newErrors[id] = requiredMsg;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    await runExclusive(async () => {
      // Patient navigators skip question validation
      if (!shouldBypassBlockingGuards && !validate()) {
        return;
      }

      // Build answers payload from current state
      const answersPayload: ScreenerAnswer[] = Object.entries(QUESTION_KEY_MAP).map(([qid]) => {
        return {
          question_id: Number(qid),
          response: answers[Number(qid)] === true ? "Yes" : "No",
        };
      });

      // Post answers to backend for all roles (best-effort; do not block navigation on failure)
      if (data.userUuid) {
        try {
          await postScreenerAnswers(data.userUuid, answersPayload);
        } catch {
          // best-effort – swallow error and proceed to navigation
        }
      }

      // Publish screener answers back to Salesforce (best-effort).
      // Build the payload from local `answers` (not `data.patientScreenerAnswers`) since the
      // context write from the last `handleChange` may not have re-rendered yet.
      if (isInSalesforce && publishToSalesforce) {
        try {
          const outboundData = {
            ...data,
            patientScreenerAnswers: mapAnswersToPatientScreenerAnswers(answers),
          };
          publishToSalesforce(
            CANVAS_OUTBOUND_EVENT_NAME,
            buildOutboundPayload(outboundData, APP_ROUTES.SCREENER),
          );
        } catch {
          // best-effort – swallow error and proceed to navigation
        }
      }

      // Patient navigators proceed to find-provider regardless of screener answers
      if (shouldBypassBlockingGuards) {
        navigate(APP_ROUTES.FIND_PROVIDER, { state: { fromScreener: true } });
        return;
      }

      // Non-navigators: redirect to contact-us if ANY exclusionary question (1–5) is Yes
      const anyExclusionaryYes = [1, 2, 3, 4, 5].some((qid) => {
        return answers[qid] === true;
      });
      if (anyExclusionaryYes) {
        navigateToContactUs(navigate, data.userUuid, undefined, false);
        return;
      }

      // All other scenarios: proceed to find-provider
      navigate(APP_ROUTES.FIND_PROVIDER, { state: { fromScreener: true } });
    });
  };

  return (
    <form
      id="obc-patient-screener-form"
      data-testid="test-id-patient-screener-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <Card
        data-testid="test-id-patient-screener-card"
        css={{
          width: "100%",
          padding: isMobile ? "1.5rem" : "2rem",
        }}
      >
        {questions.map(({ id, label, sublabels }) => {
          return (
            <Fragment key={id}>
              <QuestionBlock
                id={`obc-question-block-${id}`}
                data-testid={`test-id-patient-screener-q${id}-block`}
              >
                <QuestionContent data-testid={`test-id-patient-screener-q${id}-content`}>
                  <Text
                    data-testid={`test-id-patient-screener-q${id}-label`}
                    css={{
                      flex: 1,
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "1.5",
                      color: "$app.color.neutral.dark",
                    }}
                  >
                    {label}
                    <RequiredMarker aria-hidden="true">{" *"}</RequiredMarker>
                  </Text>

                  {sublabels && (
                    <ul
                      style={{
                        margin: "4px 0 0 20px",
                        padding: 0,
                        fontSize: "15px",
                        fontWeight: "normal",
                        listStyleType: "disc",
                      }}
                    >
                      {sublabels.map((item) => {
                        const parenIdx = item.indexOf(" (");
                        const boldPart = parenIdx === -1 ? item : item.slice(0, parenIdx);
                        const rest = parenIdx === -1 ? "" : item.slice(parenIdx);
                        return (
                          <li key={item}>
                            <Text>
                              <strong>{boldPart}</strong>
                              {rest}
                            </Text>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </QuestionContent>
                <ControlColumn data-testid={`test-id-patient-screener-q${id}-controls`}>
                  <ToggleInput id={id} label={label} value={answers[id]} onChange={handleChange} />
                </ControlColumn>
              </QuestionBlock>
              {errors[id] && (
                <ErrorRow
                  data-testid={`test-id-patient-screener-q${id}-error-row`}
                  aria-live="assertive"
                  aria-atomic="true"
                >
                  <IconSymbol
                    data-testid={`test-id-patient-screener-q${id}-error-icon`}
                    icon="error"
                    color="$app.color.text.status.error"
                    size="sm"
                    variant="outlined"
                  />
                  <Text
                    data-testid={`test-id-patient-screener-q${id}-error-text`}
                    css={{
                      color: "$app.color.text.status.error",
                      fontWeight: 500,
                    }}
                    role="alert"
                    id={`obc-q${id}-error`}
                  >
                    {errors[id]}
                  </Text>
                </ErrorRow>
              )}
              {id === 6 && answers[6] === true && (
                <DisclaimerRow
                  data-testid="test-id-patient-screener-q6-disclaimer"
                  role="note"
                  id="obc-q6-disclaimer"
                >
                  <IconSymbol
                    data-testid="test-id-patient-screener-q6-disclaimer-icon"
                    icon="info"
                    color="$app.color.patient.screener.disclaimer.text"
                    size="sm"
                    variant="outlined"
                  />
                  <Text
                    data-testid="test-id-patient-screener-q6-disclaimer-text"
                    css={{
                      flex: 1,
                      color: "$app.color.patient.screener.disclaimer.text",
                      fontWeight: 500,
                    }}
                  >
                    These medications may require an in-person visit at least once annually. During
                    your first visit, your provider can discuss this and other questions you may
                    have.
                  </Text>
                </DisclaimerRow>
              )}
            </Fragment>
          );
        })}
      </Card>

      <FormFooter isLoading={isPending} isUiBlocked={isUiBlocked} />
    </form>
  );
};
